import { Button } from '@sunwebgroup/uds-ui/button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'

import { useRouter } from 'next/navigation'
import { values } from 'ramda'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { buildUrlWithCurrentParams } from '@/scopes/checkout/domain/helpers/BuildUrlWithCurrentParams'
import { useBookingDraftQuery, useGetStepsTabs } from '@/scopes/checkout/domain/hooks'
import { OverviewBusSummary, OverviewGenericSummary } from '@/scopes/checkout/domain/types'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import styles from './CheckoutSummary.module.css'

export function CheckoutSummary() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()

  if (isLoading)
    return ''
  const basketInformation = data?.legacyBasketInformation
  if (basketInformation && !basketInformation.accommodationName)
    return ''
  const summariesInformation = data?.summaries
  const busSummary = summariesInformation?.find(s => s.type === 'bus') as OverviewBusSummary | undefined
  const imageUrl = basketInformation?.imageUrl ?? ''
  const { participants, groupInformation } = data ?? {}

  const startDate = basketInformation?.accommodationDates?.startDate ? new Date(basketInformation.accommodationDates.startDate) : null
  const endDate = basketInformation?.accommodationDates?.endDate ? new Date(basketInformation.accommodationDates.endDate) : null

  return (
    <CheckoutSection title="">
      <div className={cn(styles.flexContentRow, styles.flexContentGap16)}>
        <div className={styles.basketImageWrapper}>
          <MediaServerLazyImage
            src={imageUrl ?? ''}
            widths={[240, 320]}
            alt={basketInformation?.accommodationName ?? ''}
            width={320}
            loading="lazy"
          />
        </div>

        <div className={cn(styles.flexContent, styles.flexContentGap16)}>
          <div className={cn(styles.flexContent, styles.flexContentGap8)}>
            <Typography variant="display-f-regular" as="span" className={styles.accommodationName}>
              {basketInformation?.accommodationName}
            </Typography>
            <Typography variant="body-m-regular-tight" className={styles.location}>
              {basketInformation?.location}
            </Typography>
          </div>
          <div className={cn(styles.flexContent, styles.flexContentGap4)}>
            {basketInformation?.accommodationDates && (
              <Typography variant="body-m-regular-tight" className={styles.bookingDetails}>
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_overview_step_accommodation_summary' },
                  {
                    startDate: startDate?.toLocaleString(intl.locale, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                    }),
                    endDate: endDate?.toLocaleString(intl.locale, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    }),
                    duration: basketInformation?.accommodationDates?.duration,
                  },
                )}
                {!basketInformation?.accommodationDates && basketInformation?.departureDate}
              </Typography>
            )}

            <Typography variant="body-m-regular-tight" className={styles.bookingDetails}>
              {!basketInformation?.accommodationDates && basketInformation?.duration}
              {' '}
              {participants && values(participants).length > 1 && `, ${basketInformation?.occupancy}`}
            </Typography>
            {busSummary && (
              <Typography variant="body-m-regular-tight" className={styles.bookingDetails}>
                {' '}
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_overview_step_bus_summary' },
                  { outboundDate: busSummary.outboundDate, inboundDate: busSummary.inboundDate },
                )}
              </Typography>
            )}
            {groupInformation && (
              <Typography variant="body-m-regular-tight" className={styles.bookingDetails}>
                {' '}
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_basket_group_information' },
                  { groupName: groupInformation.name, groupSize: groupInformation.numberOfParticipants },
                )}
              </Typography>
            )}
          </div>
          <div className={cn(styles.flexContent, styles.flexContentGap8)}>
            <Typography variant="body-m-bold-tight" className={styles.bookingDetails}>
              {intl.formatMessage(
                { id: 'groups_checkout_steps_overview_step_total_price' },
                { totalPrice: basketInformation?.totalPrice },
              )}
            </Typography>
          </div>
        </div>
      </div>
      <div className={styles.overviewContent}>
        <ParticipantDetails />
        <RoomAndTransportDetails />
        <ServicesDetails />
      </div>
    </CheckoutSection>
  )
}

type ParticipantTitleType = 'mr' | 'ms'

function participantTitleTextKeys(key: ParticipantTitleType): string | undefined {
  const mapping: Record<string, string> = {
    mr: 'checkout_steps_participants_form_field_title_mr_text',
    ms: 'checkout_steps_participants_form_field_title_ms_text',
  }
  return mapping[key]
}

function ParticipantDetails() {
  const intl = useIntl()
  const { isLoading, isFetching, data } = useBookingDraftQuery()
  const { items: stepItems, activeStep } = useGetStepsTabs()
  const router = useRouter()

  if (isLoading || isFetching)
    return ''
  const personalDetailsStep = stepItems[stepItems.findIndex(step => step.text === activeStep) - 1]
  const participantsData = data?.participants

  const handleEditClick = () => {
    if (!personalDetailsStep)
      return ''
    router.push(buildUrlWithCurrentParams(personalDetailsStep.url))
  }

  if (!participantsData)
    return ''

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      <Typography variant="body-l-bold-tight">
        {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_participants_summary_title' })}
      </Typography>
      <div className={cn(styles.flexContent, styles.flexContentGap8)}>
        {Object.values(participantsData).map((participant) => {
          if (!participant.name)
            return ''
          const participantTitleKey = participant?.title ? participantTitleTextKeys(participant.title as ParticipantTitleType) : undefined

          return (
            <div className={cn(styles.flexContent, styles.flexContentGap4)} key={participant.id}>
              {participant.name && (
                <Typography variant="body-m-regular-tight">
                  {intl.formatMessageIfExists({
                    id: participantTitleKey,
                  })}
                  {' '}
                  {participant.name}
                  {' '}
                  {participant.surname}
                </Typography>
              )}
              {participant.birthdate && (
                <Typography variant="body-m-regular-tight">{formatDate(new Date(participant.birthdate))}</Typography>
              )}
              {participant.mobilePhoneNumber && <PhoneNumber phone={participant.mobilePhoneNumber} />}
              {participant.emergencyPhoneNumber && <PhoneNumber phone={participant.emergencyPhoneNumber} />}
              {participant.email && <Typography variant="body-m-regular-tight">{participant.email}</Typography>}
              <Button className={styles.editButton} variant="outlinedNeutral" size="S" onClick={handleEditClick}>
                {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_participants_summary_edit_button' })}
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0') // leading zero
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

function PhoneNumber({ phone }: { phone: { countryCode: string, phoneNumber: string } }) {
  const { countryCode, phoneNumber } = phone
  return (
    <Typography variant="body-m-regular-tight">
      +
      {countryCode}
      {phoneNumber}
    </Typography>
  )
}
function RoomAndTransportDetails() {
  const { isLoading } = useBookingDraftQuery()
  if (isLoading)
    return ''

  return <></>
}

function ServicesDetails() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  const { items: stepItems, activeStep } = useGetStepsTabs()
  const router = useRouter()

  if (isLoading)
    return ''
  const extrasStep = stepItems[stepItems.findIndex(step => step.text === activeStep) - 2]

  const handleEditClick = () => {
    if (!extrasStep)
      return ''
    router.push(buildUrlWithCurrentParams(extrasStep.url))
  }

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      <Typography variant="body-l-bold-tight">
        {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_services_summary_title' })}
      </Typography>
      <div className={cn(styles.flexContent, styles.flexContentGap4)}>
        {data?.summaries
          ?.filter(s => s.type === 'generic')
          .map((service) => {
            const busService = service as OverviewGenericSummary // TypeScript cast
            return busService.lines.map((line, index) => (
              <Typography key={index} variant="body-m-regular-tight">
                {line}
              </Typography>
            ))
          })}
        <Button className={styles.editButton} variant="outlinedNeutral" size="S" onClick={handleEditClick}>
          {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_participants_summary_edit_button' })}
        </Button>
      </div>
    </div>
  )
}
